/**
 * What the Go box offers for a query, in the order that tells the user what
 * kind of thing they are about to do before they click (nav spec §4.4):
 * destinations · Search everywhere · Ask Conway. A sentence that matches no
 * destination is a question, so Ask leads.
 *
 * Inside an installed app the app's own destinations lead and every other
 * match follows, flagged `elsewhere` so the list can say so. Filtering them out
 * left a dead end: "food" inside Inbox offered only Search everywhere, with
 * nothing on screen saying Food Diary had been hidden.
 *
 * When nothing matches, the server's best guess (`useBestGuess`) leads,
 * flagged `guess` so the row can say it is one.
 */
import { belongsToApp } from '@features/workspace/appScope';
import { destinationById, rankDestinations } from '@shared/navigation/destinations';

import type { AppSlug } from '@shared/app-identity/installedApp';
import type { Destination, RankedDestination } from '@shared/navigation/destinations';

/** One dropdown row. */
export type GoBoxRow =
  | {
      kind: 'destination';
      destination: Destination;
      /** Outside the running installed app's reach; always false in a browser tab. */
      elsewhere: boolean;
      /** The server's best guess for text that matched nothing, not a text match. */
      guess?: true;
    }
  | { kind: 'search' }
  | { kind: 'ask' };

/** What shapes the row list beyond the query itself. */
export interface GoBoxRowOptions {
  /** Inside an installed app its own destinations lead; the rest follow as `elsewhere`. */
  installedApp: AppSlug | null;
  /** Whether Ask Conway is offered at all (apps without Ask do not). */
  showAsk: boolean;
  /** The server's best guess, used only when no destination matched. */
  guessId?: string | null;
}

/** How many destination rows the box shows at most. */
const GO_BOX_LIMIT = 6;

/** Whitespace and more than eight characters: a sentence, not a place name. */
export function looksLikeAQuestion(query: string): boolean {
  const q = query.trim();
  return q.length > 8 && /\s/.test(q);
}

/**
 * The guess as a row, or none. Chat is dropped wherever Ask is offered: Ask
 * opens Chat *with* the text, which beats opening it empty.
 */
function guessRow(
  guessId: string | null | undefined,
  installedApp: AppSlug | null,
  showAsk: boolean
): GoBoxRow[] {
  const destination = guessId ? destinationById(guessId) : undefined;
  if (!destination || destination.id === 'areas') return [];
  if (showAsk && destination.id === 'chat') return [];
  const elsewhere = !belongsToApp(destination.path, installedApp);
  return [{ kind: 'destination', destination, elsewhere, guess: true }];
}

/**
 * Matches as destination rows: the app's own exact matches, then exact ones
 * elsewhere, then typo matches the same way round. Own-first stops six
 * out-of-reach hits crowding out the app's rows; exact-before-typo stops a
 * typo in the app ("food" ≈ "fold" in Labels' "folders") burying the page
 * that was actually typed.
 */
function orderedMatches(ranked: RankedDestination[], installedApp: AppSlug | null): GoBoxRow[] {
  const tagged = ranked.map(r => ({
    ...r,
    elsewhere: !belongsToApp(r.destination.path, installedApp),
  }));
  const group = (fuzzy: boolean, elsewhere: boolean): GoBoxRow[] =>
    tagged
      .filter(t => t.fuzzy === fuzzy && t.elsewhere === elsewhere)
      .map(t => ({ kind: 'destination', destination: t.destination, elsewhere }));
  return [
    ...group(false, false),
    ...group(false, true),
    ...group(true, false),
    ...group(true, true),
  ];
}

/** The rows for `query`; empty for an empty query. */
export function buildGoBoxRows(
  query: string,
  { installedApp, showAsk, guessId }: GoBoxRowOptions
): GoBoxRow[] {
  if (!query.trim()) return [];
  const rows = orderedMatches(rankDestinations(query), installedApp).slice(0, GO_BOX_LIMIT);
  const ask: GoBoxRow[] = showAsk ? [{ kind: 'ask' }] : [];
  if (rows.length > 0) return [...rows, { kind: 'search' }, ...ask];
  // Nothing matched: the guess leads, and a sentence is a question, so Ask comes before Search.
  const guess = guessRow(guessId, installedApp, showAsk);
  if (showAsk && looksLikeAQuestion(query)) return [...guess, ...ask, { kind: 'search' }];
  return [...guess, { kind: 'search' }, ...ask];
}
